import type { FilterDefinition, FilterKey } from "./types";

type MutableRequest<Request extends object> = Record<string, unknown> &
  Partial<Request>;

const TABLE_STATE_KEYS = [
  "page",
  "perPage",
  "sortBy",
  "sortDirection",
] as const;

export interface FilterUrlOptions {
  namespace?: string;
  includeTableState?: boolean;
}

export interface QuerySegment<Request extends object = any> {
  definitions: readonly FilterDefinition<Request>[];
  values: Partial<Request>;
  normalize?: boolean;
}

function namespacePrefix(namespace?: string): string | undefined {
  return namespace ? `${namespace}.` : undefined;
}

function logicalUrlParams<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  params: URLSearchParams,
  options: FilterUrlOptions,
): URLSearchParams {
  const prefix = namespacePrefix(options.namespace);
  if (!prefix) return new URLSearchParams(params.toString());

  const logical = new URLSearchParams();
  params.forEach((value, key) => {
    if (key.startsWith(prefix)) logical.append(key.slice(prefix.length), value);
  });

  return logical;
}

function applyLogicalUrlParams(
  current: URLSearchParams,
  logical: URLSearchParams,
  options: FilterUrlOptions,
): URLSearchParams {
  const prefix = namespacePrefix(options.namespace);
  if (!prefix) return logical;

  const next = new URLSearchParams(current.toString());
  Array.from(next.keys()).forEach((key) => {
    if (key.startsWith(prefix)) next.delete(key);
  });
  logical.forEach((value, key) => next.append(`${prefix}${key}`, value));
  return next;
}

function parseTableState<Request extends object>(
  params: URLSearchParams,
  defaults: Request,
): Partial<Request> {
  const parsed: Record<string, unknown> = {};
  const defaultValues = defaults as Record<string, unknown>;

  TABLE_STATE_KEYS.forEach((key) => {
    if (!(key in defaultValues)) return;
    const raw = params.get(key);
    if (raw === null) return;
    if (key === "page" || key === "perPage") {
      const value = Number(raw);
      if (Number.isInteger(value) && value > 0) parsed[key] = value;
    } else if (key === "sortDirection") {
      if (raw === "asc" || raw === "desc") parsed[key] = raw;
    } else if (raw) {
      parsed[key] = raw;
    }
  });

  return parsed as Partial<Request>;
}

function writeTableState<Request extends object>(
  params: URLSearchParams,
  request: Request,
): void {
  const values = request as Record<string, unknown>;
  TABLE_STATE_KEYS.forEach((key) => {
    params.delete(key);
    const value = values[key];
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
}

/**
 * The request values no filter definition owns and the table state doesn't
 * cover — e.g. a cohort's saved `completedAppointmentDateRange`, which the
 * patients filter bar has no control for.
 *
 * These never round-trip through the URL, because only definitions and table
 * state are serialized there. Anything rebuilt from the URL alone therefore
 * has to carry them over explicitly or they are silently dropped, turning a
 * filtered request into an unfiltered one.
 */
export function unmanagedFilterValues<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  request: Partial<Request>,
): Partial<Request> {
  const managed = new Set<string>([
    ...TABLE_STATE_KEYS,
    ...definitions.flatMap(
      (definition) => definition.requestKeys as readonly string[],
    ),
  ]);
  return Object.fromEntries(
    Object.entries(request).filter(
      ([key, value]) => !managed.has(key) && value !== undefined,
    ),
  ) as Partial<Request>;
}

export function normalizeFilterRequest<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  request: Partial<Request>,
): Request {
  let normalized = { ...request } as Partial<Request>;
  definitions.forEach((definition) => {
    normalized = definition.normalizeRequest(normalized);
  });

  const cleaned = Object.fromEntries(
    Object.entries(normalized).filter(([, value]) => value !== undefined),
  );
  return cleaned as Request;
}

export function parseFilterUrl<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  params: URLSearchParams,
  options: FilterUrlOptions = {},
): Partial<Request> {
  const logical = logicalUrlParams(definitions, params, options);
  return definitions.reduce<Partial<Request>>(
    (parsed, definition) => ({ ...parsed, ...definition.parseUrl(logical) }),
    {},
  );
}

export function hydrateFilterRequest<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  params: URLSearchParams,
  defaults: Request,
  options: FilterUrlOptions = {},
): Request {
  const logical = logicalUrlParams(definitions, params, options);
  return normalizeFilterRequest(definitions, {
    ...defaults,
    ...(options.includeTableState === false
      ? {}
      : parseTableState(logical, defaults)),
    ...definitions.reduce<Partial<Request>>(
      (parsed, definition) => ({
        ...parsed,
        ...definition.parseUrl(logical),
      }),
      {},
    ),
  });
}

export function writeFilterUrl<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  currentParams: URLSearchParams,
  request: Request,
  options: FilterUrlOptions = {},
): URLSearchParams {
  const logical = logicalUrlParams(definitions, currentParams, options);
  definitions.forEach((definition) => definition.writeUrl(logical, request));
  if (options.includeTableState !== false) writeTableState(logical, request);
  return applyLogicalUrlParams(currentParams, logical, options);
}

export function serializeFilterRequest<Request extends object>(
  request: Request,
): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(request).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          params.append(key, String(item));
        }
      });
      return;
    }
    if (typeof value === "object") {
      params.append(key, JSON.stringify(value));
      return;
    }
    params.append(key, String(value));
  });
  return params;
}

export function serializeFilterQuerySegments(
  segments: readonly QuerySegment[],
): URLSearchParams {
  const params = new URLSearchParams();
  segments.forEach(({ definitions, values, normalize = true }) => {
    const request = normalize
      ? normalizeFilterRequest(definitions, values)
      : values;
    definitions.forEach((definition) => {
      definition.writeQuery(params, request);
    });
  });
  return params;
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    const normalized = value.map(canonicalize);
    return normalized.every((item) => typeof item === "string")
      ? Array.from(new Set(normalized as string[])).sort()
      : normalized;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, nested]) => nested !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, canonicalize(nested)]),
    );
  }
  return value;
}

/**
 * Set filters are order- and duplicate-insensitive, so canonicalizing them
 * keeps equivalent selections on one cache entry. Positional tuples must be
 * left alone: deduping a same-day range like `["2026-08-03", "2026-08-03"]`
 * leaves one bound, which the range contract then rejects — dropping the
 * filter from the request entirely. Callers that pass definitions get that
 * distinction; callers that don't canonicalize every array.
 */
export function canonicalFilterKey<Request extends object>(
  request: Request,
  definitions?: readonly FilterDefinition<Request>[],
): Request {
  if (!definitions) return canonicalize(request) as Request;
  const setKeys = new Set(
    definitions
      .filter((definition) => definition.value === "set")
      .flatMap((definition) => definition.requestKeys as readonly string[]),
  );
  return Object.fromEntries(
    Object.entries(request)
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, value]) => [
        key,
        setKeys.has(key) ? canonicalize(value) : value,
      ]),
  ) as Request;
}

export function projectFilterControls<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  request: Request,
): Record<string, unknown> {
  return Object.fromEntries(
    definitions
      .filter((definition) => definition.presented)
      .map((definition) => [definition.key, definition.readControl(request)]),
  );
}

export function applyControlValue<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  defaults: Request,
  request: Request,
  key: string,
  value: unknown,
  resetPageOnChange: boolean = true,
): Request {
  const definition = definitions.find((candidate) => candidate.key === key);
  if (!definition) return request;
  const changed = definition.writeControl(request, value);
  const resetPage =
    resetPageOnChange &&
    ("page" in (defaults as object) || "page" in (request as object));
  return normalizeFilterRequest(definitions, {
    ...defaults,
    ...changed,
    ...(resetPage ? { page: 1 } : {}),
  } as Partial<Request>);
}

export function applyFilterValue<
  Request extends object,
  Key extends FilterKey<Request>,
>(
  definitions: readonly FilterDefinition<Request>[],
  defaults: Request,
  request: Request,
  key: Key,
  value: Request[Key] | undefined,
  resetPageOnChange: boolean = true,
): Request {
  const definition = definitions.find((candidate) => candidate.key === key);
  // An empty set clears the filter just like `undefined`, so it has to go
  // through writeControl too — that is what resets companion keys such as a
  // location filter's includeUnassigned flag.
  const cleared =
    value === undefined || (Array.isArray(value) && value.length === 0);
  const changed =
    cleared && definition
      ? definition.writeControl(request, undefined)
      : ({ ...request, [key]: value } as Partial<Request>);
  const resetPage =
    resetPageOnChange &&
    ("page" in (defaults as object) || "page" in (request as object));
  return normalizeFilterRequest(definitions, {
    ...defaults,
    ...changed,
    ...(resetPage ? { page: 1 } : {}),
  } as Partial<Request>);
}

export function isFilterActive<Request extends object>(
  definitions: readonly FilterDefinition<Request>[],
  request: Request,
  key: FilterKey<Request>,
): boolean {
  const definition = definitions.find((candidate) =>
    candidate.requestKeys.includes(key),
  );
  if (!definition) return false;
  const values = request as MutableRequest<Request>;
  return definition.requestKeys.some((requestKey) => {
    const value = values[requestKey];
    return !(
      value === undefined ||
      value === null ||
      value === "" ||
      value === false ||
      (Array.isArray(value) && value.length === 0)
    );
  });
}
